// Ghi <workspace>/.claude/settings.json — schema Claude Code expect.
// 4 branch input: subscription / llmlite / anthropic / use-global.
// Merge-safe: preserve key user thêm thủ công (vd "theme", "permissions"),
// chỉ override env.ANTHROPIC_BASE_URL / env.ANTHROPIC_AUTH_TOKEN / model.
// Atomic write qua tmp-rename + chmod 600 (file chứa secret token).
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { pathExists, readJson, writeJsonAtomic } from "./filesystem-helpers.js";

// chmod 600 = owner read/write only. Bắt buộc cho file chứa raw token.
const SECRET_FILE_MODE = 0o600;

// 4 branch input — discriminated union để TS narrow chính xác.
//   - subscription : OAuth Pro/Max, không cần key
//   - llmlite      : NAL gateway (ai.nal.vn), dùng ANTHROPIC_AUTH_TOKEN (Bearer)
//   - anthropic    : Anthropic direct, dùng ANTHROPIC_API_KEY (x-api-key) (v1.6.0)
//   - use-global   : copy raw từ ~/.claude/settings.json
//
// v1.20.0: skipApiKey=true → keys lưu ở .envrc thay vì settings.json plaintext.
// settings.json chỉ giữ non-secret config (provider label, baseUrl, model).
// direnv tự load env var từ .envrc khi cd vào workspace.
//
// v1.20.3: Codex/Gemini providers removed — Claude Code native không support.
// Để lưu OPENAI_API_KEY / GEMINI_API_KEY cho skills khác → 'avatar secrets set'.
export type ClaudeSettingsInput =
  | { provider: "subscription"; model: string }
  | { provider: "llmlite"; apiKey: string; baseUrl: string; model: string; skipApiKey?: boolean }
  | { provider: "anthropic"; apiKey: string; baseUrl: string; model: string; skipApiKey?: boolean }
  | { provider: "use-global"; sourceSettings: Record<string, unknown> };

// Shape minimal của Claude Code settings.json — chỉ key Avatar đụng tới.
// Index signature cho phép user thêm key custom (permissions, theme...) mà không lỗi type.
interface ClaudeSettings {
  env?: {
    ANTHROPIC_BASE_URL?: string;
    ANTHROPIC_AUTH_TOKEN?: string; // LLMLite gateway (Bearer auth)
    ANTHROPIC_API_KEY?: string; // Anthropic direct (x-api-key auth) — v1.6.0
    [k: string]: unknown;
  };
  model?: string;
  // v1.20.0: provider label (informational only, Claude Code không đọc).
  avatarProvider?: string;
  [k: string]: unknown;
}

// Alias→bare-model override keys do ai-orchestrator ghi (xem
// resolve-claude-code-alias-to-bare-model-env.ts). Chúng map alias opus/sonnet/haiku
// của Claude Code sang model thật theo tier.
// PHẢI strip khi đổi provider: nếu để sót, alias (vd model="opus") vẫn resolve về
// model provider CŨ (vd deepseek) dù đã chuyển subscription/anthropic → chạy nhầm model.
// (Bug thật: switch LLMLite/deepseek → subscription vẫn hỏi ra deepseek.)
export const ALIAS_MODEL_OVERRIDE_KEYS = [
  "ANTHROPIC_DEFAULT_OPUS_MODEL",
  "ANTHROPIC_DEFAULT_SONNET_MODEL",
  "ANTHROPIC_DEFAULT_HAIKU_MODEL",
] as const;

// Credential/endpoint keys của provider — mỗi provider tự set lại cái mình cần.
export const PROVIDER_CREDENTIAL_KEYS = [
  "ANTHROPIC_BASE_URL",
  "ANTHROPIC_AUTH_TOKEN",
  "ANTHROPIC_API_KEY",
] as const;

// Xóa danh sách key khỏi env object (không mutate input). Undefined-safe.
export function omitEnvKeys(
  env: ClaudeSettings["env"],
  keys: readonly string[],
): ClaudeSettings["env"] {
  if (!env) return env;
  const out: Record<string, unknown> = { ...env };
  for (const k of keys) delete out[k];
  return out as ClaudeSettings["env"];
}

// Đường dẫn settings target trong workspace.
export function getClaudeSettingsPath(workspacePath: string): string {
  return join(workspacePath, ".claude", "settings.json");
}

// Đọc settings hiện có nếu file tồn tại. Throw nếu JSON corrupted để caller báo user
// (an toàn hơn merge với object rỗng — có thể nuốt config user đã có).
async function readExistingSettings(path: string): Promise<ClaudeSettings> {
  if (!(await pathExists(path))) return {};
  try {
    return await readJson<ClaudeSettings>(path);
  } catch (err) {
    throw new Error(
      `Không parse được ${path} (JSON lỗi): ${(err as Error).message}. Backup file rồi xóa để Avatar tạo lại.`,
    );
  }
}

// Subscription path: clear MỌI provider env keys (credential + alias-model override
// nếu có từ config cũ), set model. OAuth dùng quota cá nhân, không cần env var.
function applySubscription(existing: ClaudeSettings, model: string): ClaudeSettings {
  const { env: existingEnv, ...rest } = existing;
  const merged: ClaudeSettings = { ...rest, model };
  const envRest = omitEnvKeys(existingEnv, [
    ...PROVIDER_CREDENTIAL_KEYS,
    ...ALIAS_MODEL_OVERRIDE_KEYS,
  ]);
  if (envRest && Object.keys(envRest).length > 0) {
    merged.env = envRest;
  }
  return merged;
}

// Drop all provider-specific keys (env vars that might conflict between providers).
// Per-provider apply functions add back the ones THIS provider needs.
// BASE_URL không strip ở đây vì caller (llmlite/anthropic) ghi đè ngay sau đó;
// alias-model override PHẢI strip để không kế thừa map model của provider cũ.
function stripAllProviderKeys(env: ClaudeSettings["env"]): ClaudeSettings["env"] {
  return omitEnvKeys(env, [
    // BASE_URL loại khỏi list vì caller ghi đè ngay sau; các credential key khác + alias override đều strip.
    ...PROVIDER_CREDENTIAL_KEYS.filter((k) => k !== "ANTHROPIC_BASE_URL"),
    ...ALIAS_MODEL_OVERRIDE_KEYS,
  ]);
}

// LLMLite path: ANTHROPIC_AUTH_TOKEN (Bearer auth) + baseUrl gateway + model.
// v1.20.0: if skipApiKey=true, omit token field (direnv .envrc handles it).
function applyLLMLite(
  existing: ClaudeSettings,
  apiKey: string,
  baseUrl: string,
  model: string,
  skipApiKey: boolean,
): ClaudeSettings {
  const envRest = stripAllProviderKeys(existing.env);
  const env: ClaudeSettings["env"] = {
    ...envRest,
    ANTHROPIC_BASE_URL: baseUrl,
  };
  if (!skipApiKey) env.ANTHROPIC_AUTH_TOKEN = apiKey;
  return { ...existing, env, model, avatarProvider: "llmlite" };
}

function applyAnthropic(
  existing: ClaudeSettings,
  apiKey: string,
  baseUrl: string,
  model: string,
  skipApiKey: boolean,
): ClaudeSettings {
  const envRest = stripAllProviderKeys(existing.env);
  const env: ClaudeSettings["env"] = {
    ...envRest,
    ANTHROPIC_BASE_URL: baseUrl,
  };
  if (!skipApiKey) env.ANTHROPIC_API_KEY = apiKey;
  return { ...existing, env, model, avatarProvider: "anthropic" };
}

// Use-global path: copy block env + model từ ~/.claude/settings.json, giữ key local khác.
// Strip credential + alias-model override CŨ của workspace TRƯỚC khi merge env global:
// nếu không, key provider cũ (vd deepseek BASE_URL / ANTHROPIC_DEFAULT_*_MODEL) mà global
// không có sẽ sót lại → vẫn hijack provider (cùng lớp bug với subscription/llmlite/anthropic).
function applyUseGlobal(existing: ClaudeSettings, source: Record<string, unknown>): ClaudeSettings {
  const sourceEnv = (source.env as Record<string, unknown> | undefined) || {};
  const sourceModel = typeof source.model === "string" ? source.model : undefined;
  const existingEnvStripped = omitEnvKeys(existing.env, [
    ...PROVIDER_CREDENTIAL_KEYS,
    ...ALIAS_MODEL_OVERRIDE_KEYS,
  ]);
  const mergedEnv = { ...(existingEnvStripped || {}), ...sourceEnv };
  const result: ClaudeSettings = {
    ...existing,
    ...(sourceModel ? { model: sourceModel } : {}),
  };
  if (Object.keys(mergedEnv).length > 0) {
    result.env = mergedEnv as ClaudeSettings["env"];
  } else {
    delete result.env;
  }
  return result;
}

// Main API. Ghi settings + chmod 600. Throws nếu file corrupted hoặc IO fail.
export async function writeClaudeSettings(
  workspacePath: string,
  input: ClaudeSettingsInput,
): Promise<{ path: string; mode: number }> {
  const path = getClaudeSettingsPath(workspacePath);
  const existing = await readExistingSettings(path);

  let merged: ClaudeSettings;
  switch (input.provider) {
    case "subscription":
      merged = applySubscription(existing, input.model);
      break;
    case "llmlite":
      merged = applyLLMLite(
        existing,
        input.apiKey,
        input.baseUrl,
        input.model,
        input.skipApiKey === true,
      );
      break;
    case "anthropic":
      merged = applyAnthropic(
        existing,
        input.apiKey,
        input.baseUrl,
        input.model,
        input.skipApiKey === true,
      );
      break;
    case "use-global":
      merged = applyUseGlobal(existing, input.sourceSettings);
      break;
  }

  await writeJsonAtomic(path, merged, SECRET_FILE_MODE);

  // Re-chmod sau write — writeJsonAtomic đã apply mode trên tmp, rename giữ mode trên POSIX.
  // Trên Windows chmod no-op, không lỗi.
  try {
    await fs.chmod(path, SECRET_FILE_MODE);
  } catch {
    // Windows hoặc filesystem không support — bỏ qua, không fail flow.
  }

  return { path, mode: SECRET_FILE_MODE };
}
